/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useReportDisplay } from "./hooks";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { GridEventListener, GridRowParams, GridSortModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import CustomDateTimePicker from "../../../components/Shared/DateTimePicker/DateTimePicker";
import { resetReportFilters, setSelectedReportCreated } from "../../../store/filters/Reports/reducers";
import dayjs, { Dayjs } from "dayjs";
import ReportsFilter from "../../../components/Nodes/ReportsFilter/ReportsFilter";
import { useReportsFilter } from "../../../components/Nodes/ReportsFilter/hooks";
import { getSortBy } from "../../../utils/helpers";
import { useSelector } from "react-redux";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";
import ReportPreviewDrawer from "../../../components/Drawers/ReportPreviewDrawer";
import { IReportsResponse } from "../../../store/api/devices/types";

const Reports: React.FC = () => {
  const { dispatch, reportsColumn, rowsData, isLoading, noOfRows, reportFilterData, isReportselected, setIsReportSelected, setSortOrderToReport } =
    useReportDisplay();
  const { filters: reportFilters } = useReportsFilter();
  const reportSearchKeyword = useSelector(searchKeywordSelector);

  const handleClearReportSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllReportFilters = useCallback(() => {
    dispatch(resetReportFilters());
    dispatch(setRegionFilterIds([]));
    dispatch(resetKeyword());
  }, [dispatch]);

  const [dateSelected, setDateSelcted] = useState<any>(reportFilterData.selectedReportCreated || dayjs(new Date()));
  const [selectedReport, setSelectedReport] = useState<IReportsResponse | null>(null);
  const [openReportDrawer, setOpenReportDrawer] = useState(false);

  useEffect(() => {
    dispatch(setSelectedReportCreated(dayjs(new Date())));
  }, [dispatch]);

  useEffect(() => {
    if (reportFilterData.selectedReportCreated) {
      setDateSelcted(reportFilterData.selectedReportCreated);
    }
  }, [reportFilterData.selectedReportCreated]);

  useEffect(() => {
    if (!isReportselected) {
      const intervalId = setInterval(() => {
        setDateSelcted(dayjs(new Date()));
      }, 10000); // Update every 10 seconds

      return () => {
        clearInterval(intervalId); // Clean up the interval when the component unmounts
      };
    }
  }, [dispatch, isLoading, isReportselected]);

  const handleOnChangeCreated = (dateTime: Dayjs | null | undefined) => {
    setIsReportSelected(true);
    dispatch(setSelectedReportCreated(dateTime));
  };

  const handleReportRowClick: GridEventListener<"rowClick"> = (params: GridRowParams<any>, event: any) => {
    if ((event?.target as HTMLElement)?.closest("button")) {
      return;
    }
    setSelectedReport(params.row as IReportsResponse);
    setOpenReportDrawer(true);
  };

  return (
    <div>
      <ReportPreviewDrawer open={openReportDrawer} onClose={() => setOpenReportDrawer(false)} report={selectedReport} />
      <div>
        <ReportsFilter>
          <div className="ml-2 shrink-0 flex items-center self-center">
            <CustomDateTimePicker
              maxDateTime={dayjs()}
              minDateTime={dayjs().subtract(7, "day")}
              label="Created Date"
              value={dateSelected || dayjs(new Date())}
              handleOnChange={handleOnChangeCreated}
            />
          </div>
        </ReportsFilter>
      </div>
      <div className="flex justify-between items-end mb-1 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`Showing ${noOfRows} results`}</span>
          <ActiveFilterChips
            filters={reportFilters}
            searchKeyword={reportSearchKeyword}
            onClearSearch={handleClearReportSearch}
            onClearAll={handleClearAllReportFilters}
            dispatch={dispatch}
          />
        </div>
      </div>
      <div>
        <CustomDataGrid
          tableId="reportDisplay"
          rows={rowsData}
          columns={reportsColumn}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={false}
          getRowId={(row: any) => row.id}
          onRowClick={handleReportRowClick}
          onSortModelChange={(params: GridSortModel) => setSortOrderToReport(getSortBy(params))}
          fillContainer
        />
      </div>
    </div>
  );
};

export default Reports;
